import { isRealtimeEventModel } from "src/utils/event-models";
import { type RealtimeConditionScope } from "src/components/explore/context/hooks";

/**
 * Minimal structural shape of a parent-model relationship needed to decide
 * whether it should be offered in the filter picker. Matches the
 * `RelationshipWithoutFilterableColumns` GraphQL fragment.
 */
export type FilterableRelationship = {
  id: string | number;
  is_hidden_from_audience_filter?: boolean | null;
  through_relationships: unknown[];
  to_model: {
    event?: {
      event_model_event_sources?: readonly unknown[] | null;
    } | null;
    cep_managed_model?: {
      type: string;
    } | null;
  };
};

export type RelationshipFilterContext = {
  isAudienceCreated: boolean;
  realtimeConditionScope: RealtimeConditionScope | undefined;
  restrictToPredictorKeyEvents: boolean;
  predictorKeyEvents: Set<string>;
};

/**
 * Decides whether a relationship (to a related model or event model) should be
 * offered in the audience builder's filter picker.
 *
 * This filtering exists for UX reasons and not governance, so there's no
 * "data leak" by doing it client-side. It may be good to do this on the
 * GraphQL side instead so we fetch less resources, but doing it here keeps
 * the filtering in one place instead of applying it to multiple queries.
 */
export const shouldShowRelationship = (
  relationship: FilterableRelationship,
  context: RelationshipFilterContext,
): boolean => {
  if (relationship.is_hidden_from_audience_filter) {
    return false;
  }

  // A realtime journey expresses "this event happened" through its entry or
  // hold event trigger, never inside a condition tree, so event conditions
  // are not offered in its criteria.
  if (
    context.realtimeConditionScope === "journey-criteria" &&
    relationship.to_model.event
  ) {
    return false;
  }

  // A warehouse-backed event never arrives on the stream, so it can't trigger
  // a realtime journey.
  if (
    context.realtimeConditionScope === "journey-events" &&
    relationship.to_model.event &&
    !isRealtimeEventModel(relationship.to_model.event)
  ) {
    return false;
  }

  if (isRealtimeEventModel(relationship.to_model.event)) {
    // Streaming evaluation only supports direct parent → event relationships:
    // the event router resolves user identity via direct_model_relationships
    // and never routes events across through-relationships, so a realtime
    // event condition on a through-relationship would never be evaluated.
    if (relationship.through_relationships.length !== 0) {
      return false;
    }

    // Filter out realtime relationships if the audience has been created and
    // it's not realtime.
    return context.isAudienceCreated
      ? context.realtimeConditionScope === "audience"
      : true;
  }

  if (context.restrictToPredictorKeyEvents) {
    // Predictive traits currently support only directly related event models
    if (
      !relationship.to_model.event ||
      relationship.through_relationships.length !== 0
    ) {
      return false;
    }

    // Predictors only allow key events defined in their feature definitions
    if (!context.predictorKeyEvents.has(relationship.id.toString())) {
      return false;
    }
  }

  return true;
};
